import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Notification } from '../../types/notifications';
import { Bell, AlertCircle, Info, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

export default function NotificationsWidget() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'notifications'),
        where('recipientId', '==', user.uid),
        orderBy('createdAt', 'desc'),
        limit(3)
      ),
      (snapshot) => {
        const notificationData: Notification[] = [];
        snapshot.forEach((doc) => {
          notificationData.push({ id: doc.id, ...doc.data() } as Notification);
        });
        setNotifications(notificationData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading notifications:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'task':
        return <CheckCircle className="w-5 h-5 text-success-DEFAULT" />;
      case 'attendance':
        return <AlertCircle className="w-5 h-5 text-error-DEFAULT" />;
      case 'lms':
        return <AlertCircle className="w-5 h-5 text-warning-DEFAULT" />;
      default:
        return <Info className="w-5 h-5 text-info-DEFAULT" />;
    }
  };

  if (loading) {
    return (
      <div className="bg-surface-light rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-text-primary">Notifications</h2>
          <Bell className="w-5 h-5 text-text-secondary" />
        </div>

        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="animate-pulse flex items-start gap-4 p-4 rounded-lg bg-background"
            >
              <div className="w-5 h-5 bg-text-muted rounded-full" />
              <div className="flex-1">
                <div className="h-4 bg-text-muted rounded w-3/4 mb-2" />
                <div className="h-3 bg-text-muted rounded w-1/4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-light rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-text-primary">Notifications</h2>
        <Bell className="w-5 h-5 text-text-secondary" />
      </div>

      <div className="space-y-4">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`flex items-start gap-4 p-4 rounded-lg transition-colors ${
              notification.read ? 'bg-background' : 'bg-primary-50'
            }`}
          >
            {getIcon(notification.type)}

            <div className="flex-1">
              <p className="text-sm text-text-primary">{notification.message}</p>
              <span className="text-xs text-text-muted">
                {format(new Date(notification.createdAt), 'p')}
              </span>
            </div>
          </div>
        ))}

        {notifications.length === 0 && (
          <div className="text-center py-4">
            <p className="text-text-secondary">No new notifications</p>
          </div>
        )}
      </div>
    </div>
  );
}